/**
 * Accessibility utilities for the calendar components
 * Provides functions for keyboard navigation, focus management, and screen reader support
 */

import { KeyboardEvent } from 'react'

/**
 * Keyboard key codes for accessibility
 */
export const KEYS = {
  ENTER: 'Enter',
  SPACE: ' ',
  ESCAPE: 'Escape',
  TAB: 'Tab',
  ARROW_UP: 'ArrowUp',
  ARROW_DOWN: 'ArrowDown',
  ARROW_LEFT: 'ArrowLeft',
  ARROW_RIGHT: 'ArrowRight',
  HOME: 'Home',
  END: 'End',
  PAGE_UP: 'PageUp',
  PAGE_DOWN: 'PageDown',
}

/**
 * Handles keyboard navigation for interactive elements
 * @param event Keyboard event
 * @param onActivate Callback to execute when element is activated
 * @param onNavigate Optional callback for arrow key navigation
 */
export function handleKeyboardNavigation(
  event: KeyboardEvent<HTMLElement>,
  onActivate: () => void,
  onNavigate?: (direction: 'up' | 'down' | 'left' | 'right') => void
): void {
  switch (event.key) {
    case KEYS.ENTER:
    case KEYS.SPACE:
      event.preventDefault()
      onActivate()
      break
    case KEYS.ARROW_UP:
      if (onNavigate) {
        event.preventDefault()
        onNavigate('up')
      }
      break
    case KEYS.ARROW_DOWN:
      if (onNavigate) {
        event.preventDefault()
        onNavigate('down')
      }
      break
    case KEYS.ARROW_LEFT:
      if (onNavigate) {
        event.preventDefault()
        onNavigate('left')
      }
      break
    case KEYS.ARROW_RIGHT:
      if (onNavigate) {
        event.preventDefault()
        onNavigate('right')
      }
      break
    default:
      break
  }
}

/**
 * Creates an accessible announcement for screen readers
 * @param message Message to announce
 * @param priority Whether to announce immediately (polite or assertive)
 */
export function announceForScreenReader(
  message: string,
  priority: 'polite' | 'assertive' = 'polite'
): void {
  // Create or get the live region element
  let liveRegion = document.getElementById('calendar-live-region')

  if (!liveRegion) {
    liveRegion = document.createElement('div')
    liveRegion.id = 'calendar-live-region'
    liveRegion.className = 'sr-only'
    liveRegion.setAttribute('aria-live', priority)
    liveRegion.setAttribute('aria-relevant', 'additions')
    document.body.appendChild(liveRegion)
  }

  // Set the priority
  liveRegion.setAttribute('aria-live', priority)

  // Clear the region first (for some screen readers)
  liveRegion.textContent = ''

  // Set the announcement text after a small delay
  setTimeout(() => {
    liveRegion.textContent = message
  }, 50)
}

/**
 * Focuses an element and scrolls it into view if needed
 * @param element Element to focus
 * @param options Focus options
 */
export function focusElement(element: HTMLElement | null, options?: FocusOptions): void {
  if (element) {
    element.focus(options)

    // Check if element is in view
    const rect = element.getBoundingClientRect()
    const isInView =
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)

    // Scroll into view if needed
    if (!isInView) {
      element.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }
}

/**
 * Creates a trap focus within a container
 * @param container Container element to trap focus within
 * @param initialFocusElement Optional element to focus initially
 * @returns Function to remove the focus trap
 */
export function createFocusTrap(
  container: HTMLElement,
  initialFocusElement?: HTMLElement
): () => void {
  if (!container) return () => {}

  // Find all focusable elements
  const focusableElements = container.querySelectorAll<HTMLElement>(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  )

  const firstElement = focusableElements[0]
  const lastElement = focusableElements[focusableElements.length - 1]

  // Focus the initial element
  if (initialFocusElement) {
    initialFocusElement.focus()
  } else if (firstElement) {
    firstElement.focus()
  }

  // Handle tab key to trap focus
  const handleTabKey = (event: KeyboardEvent) => {
    if (event.key !== 'Tab') return

    // If shift+tab on first element, go to last element
    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault()
      lastElement.focus()
    }
    // If tab on last element, go to first element
    else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault()
      firstElement.focus()
    }
  }

  // Add event listener
  document.addEventListener('keydown', handleTabKey as any)

  // Return cleanup function
  return () => {
    document.removeEventListener('keydown', handleTabKey as any)
  }
}

/**
 * Generates an accessible description for an event
 * @param event Calendar event object
 * @returns Accessible description string
 */
export function getEventAccessibleDescription(event: any): string {
  const title = event.title || 'Untitled event'
  const startTime = event.start
    ? new Date(event.start).toLocaleString(undefined, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : 'No start time'

  const endTime = event.end
    ? new Date(event.end).toLocaleString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
      })
    : ''

  const timeDescription = endTime ? `${startTime} to ${endTime}` : startTime

  const gameName =
    event.extendedProps?.game?.name || event.extendedProps?.metadata?.game?.name || ''
  const gameDescription = gameName ? `Game: ${gameName}.` : ''

  const location = event.extendedProps?.location || ''
  const locationDescription = location ? `Location: ${location}.` : ''

  const maxPlayers = event.extendedProps?.maxPlayers || 0
  const currentPlayers = event.extendedProps?.participantCount || 0
  const playersDescription =
    maxPlayers > 0 ? `${currentPlayers} of ${maxPlayers} players registered.` : ''

  return `${title}. ${timeDescription}. ${gameDescription} ${locationDescription} ${playersDescription}`.trim()
}
